import { useReadQuery } from "@apollo/client";
import { GridLegacy as Grid, Box, Typography, Paper } from "@mui/material";
import { useCallback, useMemo } from "react";
import { StatusDonutChart, SubmissionStatusId } from "./StatusDonutChart";
import { TenantDonutChart } from "./TenantDonutChart";
import { StateBarChart } from "./StateBarChart";
import { AssigneeDonutChart } from "./AssigneeDonutChart";
import { useWasteFulfillmentSummaryContext } from "../WasteFulfillmentSummaryContext";
import { useOmnisearch } from "providers/OmnisearchDataContext";
import { extractOmnisearchValue } from "stores/omnisearchStore";

function stripQuotes(value: string | undefined): string | undefined {
  if (!value) return value;
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    return value.slice(1, -1);
  }
  return value;
}

/**
 * Summary component that displays key metrics and charts.
 *
 * Reads omnisearch from the OmnisearchProvider context (single source of truth).
 * Writes also go through context — no prop drilling needed.
 */
export function Summary() {
  const { omnisearch, editOmnisearchKeyValues, removeOmnisearchKeyValue } =
    useOmnisearch();

  // Get the query ref from context - this uses useBackgroundQuery under the hood
  // which prevents re-suspension on filter changes
  // isPending is true while filters are transitioning (deferred value hasn't caught up yet)
  const { summaryQueryRef, isPending } = useWasteFulfillmentSummaryContext();

  // useReadQuery reads from the query ref without triggering loading states on refetch
  // It automatically shows old data while new data loads in the background
  const { data } = useReadQuery(summaryQueryRef);

  const summary = data?.wasteSubmissionsStaffSummary;

  // Derive onlyOverdue from omnisearch
  const onlyOverdueValue = extractOmnisearchValue(omnisearch, "onlyOverdue");
  const onlyOverdue = onlyOverdueValue === "true";

  /**
   * Check if a value is a single selection (not multi-value).
   * Multi-value filters use either pipe (|) or comma (,) as separators.
   * This is a type guard that narrows the type to string.
   */
  const isSingleValue = (value: string | undefined): value is string => {
    if (!value) return false;
    return !value.includes("|") && !value.includes(",");
  };

  // Extract omnisearch key values from URL store for chart selection state
  const statusRaw = stripQuotes(extractOmnisearchValue(omnisearch, "status"));
  const selectedStatusId = useMemo((): SubmissionStatusId | null => {
    if (isSingleValue(statusRaw)) {
      return statusRaw as SubmissionStatusId;
    }
    return null;
  }, [statusRaw]);

  const tenantNameRaw = stripQuotes(
    extractOmnisearchValue(omnisearch, "tenantName")
  );
  const selectedTenantName = useMemo((): string | null => {
    if (isSingleValue(tenantNameRaw)) {
      return tenantNameRaw;
    }
    return null;
  }, [tenantNameRaw]);

  const stateRaw = stripQuotes(extractOmnisearchValue(omnisearch, "state"));
  const selectedState = useMemo((): string | null => {
    if (isSingleValue(stateRaw)) {
      return stateRaw;
    }
    return null;
  }, [stateRaw]);

  const staffAssigneeRaw = stripQuotes(
    extractOmnisearchValue(omnisearch, "staffAssignee")
  );
  const selectedAssigneeEmail = useMemo((): string | null => {
    if (isSingleValue(staffAssigneeRaw)) {
      return staffAssigneeRaw;
    }
    return null;
  }, [staffAssigneeRaw]);

  // Handle overdue toggle click
  const handleOverdueClick = useCallback(() => {
    if (onlyOverdue) {
      removeOmnisearchKeyValue("onlyOverdue");
    } else {
      editOmnisearchKeyValues([["onlyOverdue", "true"]]);
    }
  }, [onlyOverdue, editOmnisearchKeyValues, removeOmnisearchKeyValue]);

  // Handle status click from donut chart
  const handleStatusClick = useCallback(
    (statusId: SubmissionStatusId) => {
      if (statusId === selectedStatusId) {
        removeOmnisearchKeyValue("status");
        return;
      }

      editOmnisearchKeyValues([["status", statusId]]);
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue, selectedStatusId]
  );

  // Handle tenant click from donut chart
  const handleTenantClick = useCallback(
    (tenantName: string) => {
      if (tenantName === selectedTenantName) {
        removeOmnisearchKeyValue("tenantName");
        return;
      }

      editOmnisearchKeyValues([["tenantName", tenantName]]);
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue, selectedTenantName]
  );

  // Handle state click from bar chart
  const handleStateClick = useCallback(
    (state: string) => {
      if (state === selectedState) {
        removeOmnisearchKeyValue("state");
        return;
      }

      editOmnisearchKeyValues([["state", state]]);
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue, selectedState]
  );

  // Handle assignee click from donut chart
  const handleAssigneeClick = useCallback(
    (assigneeEmail: string) => {
      if (assigneeEmail === selectedAssigneeEmail) {
        removeOmnisearchKeyValue("staffAssignee");
        return;
      }

      editOmnisearchKeyValues([["staffAssignee", assigneeEmail]]);
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue, selectedAssigneeEmail]
  );

  return (
    <Grid
      container
      spacing={3}
      sx={{
        // Show reduced opacity while filters are transitioning
        // This indicates to users that data is being refetched
        opacity: isPending ? 0.6 : 1,
        transition: "opacity 0.2s ease-in-out",
      }}
    >
      {/* Key Metrics Row */}
      <Grid item xs={12}>
        <Paper elevation={0} sx={{ padding: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight={600}>
                  {summary?.total.toLocaleString() ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Submissions
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box
                textAlign="center"
                onClick={handleOverdueClick}
                sx={{
                  cursor: "pointer",
                  borderRadius: 1,
                  p: 1,
                  mx: -1,
                  transition: "background-color 0.2s",
                  backgroundColor: onlyOverdue
                    ? "action.selected"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: onlyOverdue
                      ? "action.selected"
                      : "action.hover",
                  },
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={600}
                  color={summary?.overdue ? "error.main" : "text.primary"}
                >
                  {summary?.overdue.toLocaleString() ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {onlyOverdue ? "Overdue (filtered)" : "Overdue"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Status Donut Chart */}
      <Grid item lg={6} md={6} xs={12}>
        <StatusDonutChart
          data={summary}
          loading={false}
          onStatusClick={handleStatusClick}
          selectedStatusId={selectedStatusId}
        />
      </Grid>

      {/* Tenant Donut Chart */}
      <Grid item lg={6} md={6} xs={12}>
        <TenantDonutChart
          data={summary}
          loading={false}
          onTenantClick={handleTenantClick}
          selectedTenantName={selectedTenantName}
        />
      </Grid>

      {/* State Bar Chart */}
      <Grid item lg={6} md={6} xs={12}>
        <StateBarChart
          data={summary}
          loading={false}
          onStateClick={handleStateClick}
          selectedState={selectedState}
        />
      </Grid>

      {/* Assignee Donut Chart */}
      <Grid item lg={6} md={6} xs={12}>
        <AssigneeDonutChart
          data={summary}
          loading={false}
          onAssigneeClick={handleAssigneeClick}
          selectedAssigneeEmail={selectedAssigneeEmail}
        />
      </Grid>
    </Grid>
  );
}
